import React, { useState, useEffect } from 'react';
import CitationResult from '../components/CitationResult';
import { formatURL } from '../utils/citationHelpers';

function LlmForm() {
  const [formData, setFormData] = useState({
    developer: 'OpenAI',
    llmName: 'ChatGPT',
    customDeveloper: '',
    customLlmName: '',
    year: '',
    versionType: 'number',
    versionNumber: '',
    versionDay: '',
    versionMonth: '',
    url: ''
  });
  const [result, setResult] = useState(null);
  const [liveResult, setLiveResult] = useState(null);

  const developerOptions = [
    'Amazon.com', 'Anthropic', 'Google', 'Hangzhou DeepSeek Artificial Intelligence Basic Technology Research',
    'Meta', 'Microsoft', 'Mistral AI', 'OpenAI', 'Perplexity AI', 'xAI', 'Personalizar'
  ];

  const llmNameOptions = {
    'Amazon.com': 'Amazon Nova',
    'Anthropic': 'Claude',
    'Google': 'Gemini',
    'Hangzhou DeepSeek Artificial Intelligence Basic Technology Research': 'DeepSeek',
    'Meta': 'Llama',
    'Microsoft': 'Microsoft Copilot',
    'Mistral AI': 'Mistral',
    'OpenAI': 'ChatGPT',
    'Perplexity AI': 'Perplexity',
    'xAI': 'Grok',
    'Personalizar': 'Personalizar'
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'developer') {
      // Actualizar automáticamente el LLM según la organización seleccionada
      setFormData({ ...formData, [name]: value, llmName: llmNameOptions[value] || '' });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // Genera una vista previa en tiempo real
  useEffect(() => {
    const developerValue = formData.developer === 'Personalizar' ? formData.customDeveloper : formData.developer;
    const llmNameValue = formData.llmName === 'Personalizar' ? formData.customLlmName : formData.llmName;
    
    if (developerValue && llmNameValue && formData.year) {
      generatePreview();
    } else {
      setLiveResult(null);
    }
  }, [formData]);

  const formatVersion = () => {
    if (formData.versionType === 'number' && formData.versionNumber) {
      return `versión ${formData.versionNumber}`;
    } else if (formData.versionType === 'date' && formData.versionMonth && formData.versionDay) {
      const monthNames = {
        '01': 'enero', '02': 'febrero', '03': 'marzo', '04': 'abril',
        '05': 'mayo', '06': 'junio', '07': 'julio', '08': 'agosto',
        '09': 'septiembre', '10': 'octubre', '11': 'noviembre', '12': 'diciembre'
      };
      return `versión del ${parseInt(formData.versionDay)} de ${monthNames[formData.versionMonth]}`;
    }
    return '';
  };

  const generatePreview = () => {
    const developerValue = formData.developer === 'Personalizar' ? formData.customDeveloper : formData.developer;
    const llmNameValue = formData.llmName === 'Personalizar' ? formData.customLlmName : formData.llmName;

    // Verificar si el desarrollador es una institución con sigla (formato: Nombre [SIGLA])
    const developerMatch = developerValue.match(/(.+)\s+\[(.+)\]/);

    let narrativeCitation, parentheticalCitation, narrativeSubsequent, parentheticalSubsequent;

    if (developerMatch) {
      // El desarrollador tiene sigla
      const fullName = developerMatch[1].trim();
      const abbreviation = developerMatch[2].trim();

      // Para cita narrativa con institucional con sigla: "Nombre (SIGLA, año)"
      narrativeCitation = `${fullName} (${abbreviation}, ${formData.year})`;
      
      // Para cita parentética: "(Nombre [SIGLA], año)"
      parentheticalCitation = `(${fullName} [${abbreviation}], ${formData.year})`;
      
      // Para cita narrativa subsecuente: "SIGLA (año)"
      narrativeSubsequent = `${abbreviation} (${formData.year})`;
      
      // Para cita parentética subsecuente: "(SIGLA, año)"
      parentheticalSubsequent = `(${abbreviation}, ${formData.year})`;
    } else {
      // Sin sigla
      narrativeCitation = `${developerValue} (${formData.year})`;
      parentheticalCitation = `(${developerValue}, ${formData.year})`;
      narrativeSubsequent = narrativeCitation;
      parentheticalSubsequent = parentheticalCitation;
    }

    const citation = {
      narrative: narrativeCitation,
      parenthetical: parentheticalCitation,
      narrativeSubsequent: narrativeSubsequent,
      parentheticalSubsequent: parentheticalSubsequent
    };

    const versionFormatted = formatVersion();
    const url = formatURL(formData.url);

    let referenceText = `${developerValue}. (${formData.year}). <em>${llmNameValue}</em>`;
    
    if (versionFormatted) {
      referenceText += ` (${versionFormatted})`;
    }
    
    referenceText += ` [Modelo de lenguaje grande]`;
    
    if (url) {
      referenceText += `. ${url}`;
    } else {
      referenceText += '.';
    }

    const reference = {
      html: referenceText,
      text: referenceText.replace(/<[^>]*>/g, '')
    };

    setLiveResult({ citation, reference });
  };

  const generateCitation = () => {
    const developerValue = formData.developer === 'Personalizar' ? formData.customDeveloper : formData.developer;
    const llmNameValue = formData.llmName === 'Personalizar' ? formData.customLlmName : formData.llmName;

    if (!developerValue || !llmNameValue || !formData.year) {
      alert('Generador APA, séptima edición dice:\n\nPor favor, completa todos los campos obligatorios.');
      return;
    }

    // Verificar si el desarrollador es una institución con sigla (formato: Nombre [SIGLA])
    const developerMatch = developerValue.match(/(.+)\s+\[(.+)\]/);

    let narrativeCitation, parentheticalCitation, narrativeSubsequent, parentheticalSubsequent;

    if (developerMatch) {
      // El desarrollador tiene sigla
      const fullName = developerMatch[1].trim();
      const abbreviation = developerMatch[2].trim();

      // Para cita narrativa con institucional con sigla: "Nombre (SIGLA, año)"
      narrativeCitation = `${fullName} (${abbreviation}, ${formData.year})`;
      
      // Para cita parentética: "(Nombre [SIGLA], año)"
      parentheticalCitation = `(${fullName} [${abbreviation}], ${formData.year})`;
      
      // Para cita narrativa subsecuente: "SIGLA (año)"
      narrativeSubsequent = `${abbreviation} (${formData.year})`;
      
      // Para cita parentética subsecuente: "(SIGLA, año)"
      parentheticalSubsequent = `(${abbreviation}, ${formData.year})`;
    } else {
      // Sin sigla
      narrativeCitation = `${developerValue} (${formData.year})`;
      parentheticalCitation = `(${developerValue}, ${formData.year})`;
      narrativeSubsequent = narrativeCitation;
      parentheticalSubsequent = parentheticalCitation;
    }

    const citation = {
      narrative: narrativeCitation,
      parenthetical: parentheticalCitation,
      narrativeSubsequent: narrativeSubsequent,
      parentheticalSubsequent: parentheticalSubsequent
    };

    const versionFormatted = formatVersion();
    const url = formatURL(formData.url);

    let referenceText = `${developerValue}. (${formData.year}). <em>${llmNameValue}</em>`;
    
    if (versionFormatted) {
      referenceText += ` (${versionFormatted})`;
    }
    
    referenceText += ` [Modelo de lenguaje grande]`;
    
    if (url) {
      referenceText += `. ${url}`;
    } else {
      referenceText += '.';
    }

    const reference = {
      html: referenceText,
      text: referenceText.replace(/<[^>]*>/g, '')
    };

    setResult({ citation, reference });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 dark:text-gray-100">LLM (inteligencia artificial)</h1>
        <p className="text-gray-600 dark:text-gray-400">Modelos de lenguaje grande, asistentes IA, chatbots</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6 dark:bg-gray-800 dark:border-gray-700">
          <div>
            <label htmlFor="developer" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Organización desarrolladora *
            </label>
            <select
              id="developer"
              name="developer"
              value={formData.developer}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              required
            >
              {developerOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {formData.developer === 'Personalizar' && (
            <div>
              <label htmlFor="customDeveloper" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Nombre de la organización *
              </label>
              <input
                type="text"
                id="customDeveloper"
                name="customDeveloper"
                value={formData.customDeveloper}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                required
              />
            </div>
          )}

          <div>
            <label htmlFor="llmName" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Nombre del LLM *
            </label>
            {formData.developer === 'Personalizar' ? (
              <input
                type="text"
                id="customLlmName"
                name="customLlmName"
                value={formData.customLlmName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                required
              />
            ) : (
              <input
                type="text"
                id="llmName"
                name="llmName"
                value={formData.llmName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                readOnly
              />
            )}
          </div>

          <div>
            <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Año de la versión *
            </label>
            <input
              type="number"
              id="year"
              name="year"
              value={formData.year}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Tipo de versión
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="versionType"
                  value="number"
                  checked={formData.versionType === 'number'}
                  onChange={handleInputChange}
                  className="mr-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <span className="dark:text-gray-300">Número de versión</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="versionType"
                  value="date"
                  checked={formData.versionType === 'date'}
                  onChange={handleInputChange}
                  className="mr-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <span className="dark:text-gray-300">Fecha de versión</span>
              </label>
            </div>
          </div>

          {formData.versionType === 'number' ? (
            <div>
              <label htmlFor="versionNumber" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Número de versión
              </label>
              <input
                type="text"
                id="versionNumber"
                name="versionNumber"
                value={formData.versionNumber}
                onChange={handleInputChange}
                placeholder="ej. 6.5"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="versionDay" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                  Día
                </label>
                <input
                  type="number"
                  id="versionDay"
                  name="versionDay"
                  value={formData.versionDay}
                  onChange={handleInputChange}
                  min="1"
                  max="31"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                />
              </div>
              <div>
                <label htmlFor="versionMonth" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                  Mes
                </label>
                <select
                  id="versionMonth"
                  name="versionMonth"
                  value={formData.versionMonth}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                >
                  <option value="">Seleccionar</option>
                  <option value="01">Enero</option>
                  <option value="02">Febrero</option>
                  <option value="03">Marzo</option>
                  <option value="04">Abril</option>
                  <option value="05">Mayo</option>
                  <option value="06">Junio</option>
                  <option value="07">Julio</option>
                  <option value="08">Agosto</option>
                  <option value="09">Septiembre</option>
                  <option value="10">Octubre</option>
                  <option value="11">Noviembre</option>
                  <option value="12">Diciembre</option>
                </select>
              </div>
            </div>
          )}

          <div>
            <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              URL *
            </label>
            <input
              type="text"
              id="url"
              name="url"
              value={formData.url}
              onChange={handleInputChange}
              placeholder="ej. https://chat.openai.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              required
            />
          </div>

          <button
            type="button"
            onClick={generateCitation}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Generar cita y referencia
          </button>
        </div>

        <div className="space-y-6">
          {!result && liveResult && (
            <CitationResult
              citation={liveResult.citation}
              reference={liveResult.reference}
              livePreview={true}
            />
          )}
          <CitationResult
            citation={result?.citation}
            reference={result?.reference}
          />
        </div>
      </div>
    </div>
  );
}

export default LlmForm;